/**
 * FilterDropdown Component
 *
 * A reusable dropdown filter component for filtering data by various criteria
 * (e.g., "All Areas", "All Statuses", "All Clients", "All Leaders", "Open Date")
 *
 * Design Principles:
 * - Clean dropdown interface
 * - Consistent styling with other form elements
 * - Accessible with proper ARIA attributes
 * - Supports single and multi-select
 * - Optional in-menu search for long option lists (e.g. leaders)
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './FilterDropdown.css';

export interface FilterOption {
  /** Value of the option */
  value: string;
  /** Display label - can be string or ReactNode for custom formatting */
  label: string | React.ReactNode;
  /** Whether option is disabled */
  disabled?: boolean;
}

export interface FilterDropdownProps {
  /** Label for the filter */
  label: string;
  /** Array of filter options */
  options: FilterOption[];
  /** Currently selected value(s) */
  value: string | string[];
  /** Callback when selection changes */
  onChange: (value: string | string[]) => void;
  /** Whether multiple selections are allowed */
  multiple?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** Show a search input inside the open menu to filter options */
  searchable?: boolean;
  /** Placeholder for the in-menu search input */
  searchPlaceholder?: string;
}

const optionSearchText = (option: FilterOption): string => {
  if (typeof option.label === 'string') return option.label;
  return option.value;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  multiple = false,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  searchable = false,
  searchPlaceholder = 'Search...',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchable]);

  const handleOptionClick = (optionValue: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
      onChange(newValues);
    } else {
      onChange(optionValue);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const getDisplayText = (): string | React.ReactNode => {
    if (multiple) {
      const selectedValues = Array.isArray(value) ? value : [];
      if (selectedValues.length === 0) return placeholder;
      if (selectedValues.length === 1) {
        const option = options.find((opt) => opt.value === selectedValues[0]);
        return option?.label || placeholder;
      }
      return `${selectedValues.length} selected`;
    }
    const option = options.find((opt) => opt.value === value);
    return option?.label || placeholder;
  };

  const isOptionSelected = (optionValue: string): boolean => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      return currentValues.includes(optionValue);
    }
    return value === optionValue;
  };

  const visibleOptions = React.useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const q = searchQuery.trim().toLowerCase();
    return options.filter((option) =>
      optionSearchText(option).toLowerCase().includes(q)
    );
  }, [options, searchable, searchQuery]);

  return (
    <div className={`filter-dropdown ${className}`} ref={dropdownRef}>
      <label className="filter-dropdown-label">{label}</label>
      <div className="filter-dropdown-wrapper">
        <button
          className={`filter-dropdown-button ${isOpen ? 'filter-dropdown-open' : ''} ${disabled ? 'filter-dropdown-disabled' : ''}`}
          onClick={() => {
            if (disabled) return;
            setIsOpen((open) => {
              if (open) setSearchQuery('');
              return !open;
            });
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`${label || placeholder} filter`}
        >
          <span className="filter-dropdown-text">{getDisplayText()}</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`filter-dropdown-icon ${isOpen ? 'filter-dropdown-icon-open' : ''}`}
          />
        </button>
        {isOpen && (
          <div className="filter-dropdown-menu" role="listbox">
            {searchable && (
              <div
                className="filter-dropdown-search"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  className="filter-dropdown-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {visibleOptions.length === 0 ? (
              <div className="filter-dropdown-option filter-dropdown-option-disabled">
                <span className="filter-dropdown-option-label">No matches</span>
              </div>
            ) : (
              visibleOptions.map((option) => (
                <div
                  key={option.value}
                  className={`filter-dropdown-option ${isOptionSelected(option.value) ? 'filter-dropdown-option-selected' : ''} ${option.disabled ? 'filter-dropdown-option-disabled' : ''}`}
                  onClick={() => !option.disabled && handleOptionClick(option.value)}
                  role="option"
                  aria-selected={isOptionSelected(option.value)}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      checked={isOptionSelected(option.value)}
                      readOnly
                      className="filter-dropdown-checkbox"
                    />
                  )}
                  <span className="filter-dropdown-option-label">{option.label}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
